import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search as SearchIcon, 
  Loader2,
  Users,
  UserPlus,
  UserCheck,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [roleFilter, setRoleFilter] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [connectingIds, setConnectingIds] = useState({});

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q, roleFilter);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery, role) => {
    setLoading(true);
    setSearched(true);
    try {
      let url = `${API_URL}/api/users?q=${encodeURIComponent(searchQuery || '')}`;
      if (role) {
        url += `&role=${role}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      // Filter out current user from results
      setResults(response.data.filter(u => u.user_id !== user?.user_id));
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ q: query });
    performSearch(query, roleFilter);
  };

  const handleRoleChange = (value) => {
    setRoleFilter(value === 'ALL' ? '' : value);
    if (searched) {
      performSearch(query, value === 'ALL' ? '' : value);
    }
  };

  const handleConnect = async (userId) => {
    setConnectingIds(prev => ({ ...prev, [userId]: true }));
    try {
      await axios.post(`${API_URL}/api/connections?receiver_id=${userId}`, {}, { withCredentials: true });
      setResults(results.map(u => 
        u.user_id === userId ? { ...u, connection_status: 'PENDING' } : u
      ));
      toast.success('Connection request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setConnectingIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'SALES': return 'bg-blue-100 text-blue-700';
      case 'HR': return 'bg-purple-100 text-purple-700';
      default: return 'bg-primary/10 text-primary';
    }
  };

  const getConnectionButton = (person) => {
    if (person.connection_status === 'ACCEPTED') {
      return (
        <Button variant="secondary" className="rounded-full" disabled>
          <UserCheck className="w-4 h-4 mr-2" />
          Connected
        </Button>
      );
    }
    if (person.connection_status === 'PENDING') {
      return (
        <Button variant="secondary" className="rounded-full" disabled>
          <Clock className="w-4 h-4 mr-2" />
          Pending
        </Button>
      );
    }
    return (
      <Button
        onClick={() => handleConnect(person.user_id)}
        disabled={connectingIds[person.user_id]}
        className="rounded-full bg-primary hover:bg-primary/90"
        data-testid={`connect-btn-${person.user_id}`}
      >
        {connectingIds[person.user_id] ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <UserPlus className="w-4 h-4 mr-2" />
            Connect
          </>
        )}
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2">Find People</h1>
          <p className="text-muted-foreground">Search for professionals to connect with</p>
        </div>

        {/* Search Form */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name, company, or headline..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-10 h-12 rounded-lg"
                  data-testid="search-query-input"
                />
              </div>
              <Select value={roleFilter || 'ALL'} onValueChange={handleRoleChange}>
                <SelectTrigger className="w-full md:w-40 h-12" data-testid="role-filter">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Roles</SelectItem>
                  <SelectItem value="SALES">Sales</SelectItem>
                  <SelectItem value="HR">HR</SelectItem>
                  <SelectItem value="BOTH">Both</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="submit"
                className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90"
                data-testid="search-submit-btn"
              >
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : !searched ? (
          <div className="empty-state">
            <SearchIcon className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Search for people</h3>
            <p className="text-muted-foreground">Enter a name, company, or keyword to find professionals</p>
          </div>
        ) : results.length === 0 ? (
          <div className="empty-state">
            <Users className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No results found</h3>
            <p className="text-muted-foreground">Try different keywords or filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{results.length} result{results.length !== 1 ? 's' : ''} found</p>
            {results.map((person, index) => (
              <Card 
                key={person.user_id} 
                className="hover:shadow-md transition-all animate-fadeIn"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`search-result-${person.user_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <Link to={`/profile/${person.user_id}`}>
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={person.picture} />
                        <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                          {getInitials(person.name)}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Link 
                          to={`/profile/${person.user_id}`}
                          className="font-semibold text-lg hover:text-primary transition-colors"
                        >
                          {person.name}
                        </Link>
                        <Badge variant="secondary" className={`text-xs ${getRoleBadgeClass(person.role)}`}>
                          {person.role}
                        </Badge>
                      </div>
                      {person.headline && (
                        <p className="text-muted-foreground mb-1">{person.headline}</p>
                      )}
                      {(person.company || person.location) && (
                        <p className="text-sm text-muted-foreground">
                          {[person.company, person.location].filter(Boolean).join(' • ')}
                        </p>
                      )}
                    </div>
                    {getConnectionButton(person)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Search;
